import { CANVAS_ZOOM_MAX, CANVAS_ZOOM_MIN } from '../constants/canvas-zoom'

/**
 * Ctrl/Cmd+wheel zoom (Chromium sends trackpad pinch as wheel + ctrlKey):
 * One rAF-batched flush so the UI stays smooth.
 * - **Mouse wheel**: readout **±3** points per base step; **faster** same‑direction notches add more
 *   steps per event from inter‑event timing (no fixed debounce — still one rAF flush per frame).
 * - **Trackpad pinch**: summed raw `deltaY` then `× exp(-k·δ)` (unchanged continuous feel).
 * If both occur in one frame: `(initial + mouseSteps×0.03) × exp(-k·δ)` then clamp.
 * Positive deltaY zooms out in both paths.
 */
const PINCH_CTRL_WHEEL_EXP_SENSITIVITY = 0.0063

/** Mouse notch: ±3 on the zoom readout (`round(z×100)`) per detent — additive on scale factor. */
const CTRL_WHEEL_MOUSE_READOUT_STEP = 0.03

/** Cap total readout steps applied in one rAF flush (prevents huge jumps if the OS coalesces many events). */
const CTRL_WHEEL_MOUSE_MAX_STEPS_PER_FLUSH = 48

/** Clamp summed wheel delta per rAF so one pathological event cannot jump past min/max in one frame. */
const PINCH_CTRL_WHEEL_ACCUM_CLAMP = 900

/**
 * Zoom level after one ctrl+wheel flush, rounded to 3 decimals: `rawMouseSteps` net mouse-notch
 * readout steps (each × {@link CTRL_WHEEL_MOUSE_READOUT_STEP}) and `rawAccum` summed trackpad
 * `deltaY`, applied to `initial` and clamped to the zoom range.
 */
export function computeCtrlWheelFlushZoom(
  rawMouseSteps: number,
  rawAccum: number,
  initial: number
): number {
  const mouseSteps = Math.max(
    -CTRL_WHEEL_MOUSE_MAX_STEPS_PER_FLUSH,
    Math.min(CTRL_WHEEL_MOUSE_MAX_STEPS_PER_FLUSH, rawMouseSteps)
  )

  const accum =
    rawAccum === 0
      ? 0
      : Math.max(-PINCH_CTRL_WHEEL_ACCUM_CLAMP, Math.min(PINCH_CTRL_WHEEL_ACCUM_CLAMP, rawAccum))

  const afterMouse = initial + mouseSteps * CTRL_WHEEL_MOUSE_READOUT_STEP
  const factor = Math.exp(-PINCH_CTRL_WHEEL_EXP_SENSITIVITY * accum)
  let working = afterMouse * factor

  working = Math.max(CANVAS_ZOOM_MIN, Math.min(CANVAS_ZOOM_MAX, working))

  return +working.toFixed(3)
}
